import { countOccupants } from 'lib/offer/occupancyUtils'
import { nonNullable } from 'lib/array/arrayUtils'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'

interface Props {
  /**
   * @default adult
   */
  adultLabel?: 'adult' | 'traveller'
  occupancies: Array<App.Occupants | App.RoomOccupants>
  /**
   * @default room
   */
  saleUnit?: string
}

function FormattedOccupancy(props: Props) {
  const { adultLabel = 'adult', occupancies, saleUnit = 'room' } = props
  const intl = useIntl()

  const { adults, children, infants, teenagers, roomCount } = useMemo(() => {
    const filteredOccupants = nonNullable(Array.isArray(occupancies) ? occupancies : [occupancies])
    const counts = countOccupants(filteredOccupants)
    return {
      ...counts,
      roomCount: filteredOccupants.length,
    }
  }, [occupancies])

  const formattedMessage = useMemo(() => {
    if (adults === 0 && children === 0 && infants === 0 && teenagers === 0) {
      return ''
    }

    // Format each occupant type separately using ICU
    const adultText = adults > 0
      ? (adultLabel === 'adult'
          ? intl.formatMessage(
              {
                id: 'nRgoUygP9y',
                defaultMessage: '{count, plural, one {# adult} other {# adults}}',
                description: 'Formatted count for adults',
              },
              { count: adults },
            )
          : intl.formatMessage(
              {
                id: 'ls3Jgv51kt',
                defaultMessage: '{count, plural, one {# traveller} other {# travellers}}',
                description: 'Formatted count for travellers',
              },
              { count: adults },
            ))
      : null

    const teenagerText = teenagers > 0
      ? intl.formatMessage(
          {
            id: '75+ZAXdLSK',
            defaultMessage: '{count, plural, one {# teenager} other {# teenagers}}',
            description: 'Formatted count for teenagers',
          },
          { count: teenagers },
        )
      : null

    const childText = children > 0
      ? intl.formatMessage(
          {
            id: '6msZTUkMTh',
            defaultMessage: '{count, plural, one {# child} other {# children}}',
            description: 'Formatted count for children',
          },
          { count: children },
        )
      : null

    const infantText = infants > 0
      ? intl.formatMessage(
          {
            id: 'RsV3UJom0G',
            defaultMessage: '{count, plural, one {# infant} other {# infants}}',
            description: 'Formatted count for infants',
          },
          { count: infants },
        )
      : null

    // Combine parts - filter out nulls and join with commas
    // This is acceptable as it's formatting logic, not translation logic
    const parts = [adultText, teenagerText, childText, infantText].filter((part): part is string => part !== null)
    const occupantsText = parts.join(', ')

    // Format rooms if saleUnit is provided
    if (!saleUnit) {
      return intl.formatMessage(
        {
          id: 'BUQaXdN7TF',
          defaultMessage: '<strong>{occupants}</strong>',
          description: 'Formatted occupancy without room count',
        },
        {
          occupants: occupantsText,
          strong: (chunks: React.ReactNode) => <strong key="occupants">{chunks}</strong>,
        },
      )
    }

    const roomsText = intl.formatMessage({
      id: 'TiKCi0McfE',
      defaultMessage: '({count, plural, one {# room} other {# rooms}})',
      description: 'Room count in parentheses for occupancy display',
    }, { count: roomCount })

    return intl.formatMessage(
      {
        id: 'Z7Wzrv3uaU',
        defaultMessage: '<strong>{occupants}</strong> {rooms}',
        description: 'Formatted occupancy with room count',
      },
      {
        occupants: occupantsText,
        rooms: roomsText,
        strong: (chunks: React.ReactNode) => <strong key="occupants">{chunks}</strong>,
      },
    )
  }, [intl, adults, children, infants, teenagers, adultLabel, saleUnit, roomCount])

  if (!formattedMessage) {
    return null
  }

  return <>{formattedMessage}</>
}

export default FormattedOccupancy
